import React, { useEffect, useMemo, useState } from "react";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiHome,
  FiImage,
  FiUsers,
  FiBookOpen,
  FiCalendar,
  FiFileText,
  FiFilePlus,
  FiSearch,
  FiGrid,
} from "react-icons/fi";
import VisitorStats from "../components/VisitorStats";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [adminEmail, setAdminEmail] = useState("");
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) navigate("/login");
    else setAdminEmail(user.email);
  }, [navigate]);

  const sections = [
    {
      title: "Tampilan Halaman Utama",
      icon: <FiHome size={28} />,
      color: "primary",
      cards: [
        {
          title: "Halaman Depan",
          desc: "Background, judul, dan deskripsi utama",
          path: "/admin/manage-home-hero",
          icon: <FiHome size={30} />,
        },
        {
          title: "Slider / Banner",
          desc: "Kelola banner & slider beranda",
          path: "/admin/manage-slider",
          icon: <FiImage size={30} />,
        },
      ],
    },
    {
      title: "Profil & SDM Sekolah",
      icon: <FiUsers size={28} />,
      color: "success",
      cards: [
        {
          title: "Profil Sekolah",
          desc: "Visi, misi, sejarah & struktur",
          path: "/admin/manage-profile",
          icon: <FiBookOpen size={30} />,
        },
        {
          title: "Guru & Staf",
          desc: "Data guru dan pegawai",
          path: "/admin/manage-guru",
          icon: <FiUsers size={30} />,
        },
        {
          title: "Statistik Sekolah",
          desc: "Jumlah siswa & guru",
          path: "/admin/manage-statistik-sekolah",
          icon: <FiGrid size={30} />,
        },
        {
          title: "Fasilitas",
          desc: "Data fasilitas sekolah",
          path: "/admin/manage-fasilitas",
          icon: <FiImage size={30} />,
        },
        {
          title: "Ekstrakurikuler",
          desc: "Kegiatan siswa",
          path: "/admin/manage-ekstrakurikuler",
          icon: <FiFileText size={30} />,
        },
        {
          title: "Kontak Sekolah",
          desc: "Alamat, telp, email & medsos",
          path: "/admin/manage-kontak",
          icon: <FiFilePlus size={30} />,
        },
      ],
    },
    {
      title: "Informasi & Media",
      icon: <FiCalendar size={28} />,
      color: "warning",
      cards: [
        {
          title: "Pengumuman & Agenda",
          desc: "Agenda & info penting",
          path: "/admin/manage-pengumuman-agenda",
          icon: <FiCalendar size={30} />,
        },
        {
          title: "Berita Sekolah",
          desc: "Kelola berita",
          path: "/admin/manage-berita",
          icon: <FiFileText size={30} />,
        },
        {
          title: "Galeri Media",
          desc: "Foto & video sekolah",
          path: "/admin/manage-galeri",
          icon: <FiImage size={30} />,
        },
      ],
    },
    {
      title: "PPDB & Siswa",
      icon: <FiFilePlus size={28} />,
      color: "danger",
      cards: [
        {
          title: "PPDB",
          desc: "Kelola informasi PPDB",
          path: "/admin/manage-ppdb",
          icon: <FiFilePlus size={30} />,
        },
        {
          title: "Pendaftar",
          desc: "Data siswa pendaftar",
          path: "/admin/manage-pendaftar",
          icon: <FiUsers size={30} />,
        },
      ],
    },
  ];

  /* 🔍 FILTER MENU */
  const filteredSections = useMemo(() => {
    if (!keyword) return sections;

    return sections
      .map((section) => ({
        ...section,
        cards: section.cards.filter(
          (c) =>
            c.title.toLowerCase().includes(keyword.toLowerCase()) ||
            c.desc.toLowerCase().includes(keyword.toLowerCase())
        ),
      }))
      .filter((s) => s.cards.length > 0);
  }, [keyword]);

  return (
    <div className="container mt-5 mb-5">
      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-4"
      >
        <h2 className="fw-bold">Dashboard Admin</h2>
        <p className="text-muted">
          Login sebagai <b>{adminEmail}</b>
        </p>
      </motion.div>

      {/* SEARCH */}
      <div className="row justify-content-center mb-4">
        <div className="col-md-6">
          <div className="input-group shadow-sm">
            <span className="input-group-text bg-white">
              <FiSearch />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Cari menu admin... (contoh: guru, ppdb, slider)"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* VISITOR STATS */}
      <VisitorStats />

      {/* SECTIONS */}
      {filteredSections.map((section, idx) => (
        <motion.div
          key={section.title}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: idx * 0.1 }}
          className="mb-5"
        >
          <h4 className={`mb-3 text-${section.color}`}>
            {section.icon} {section.title}
          </h4>

          <div className="row">
            {section.cards.map((card) => (
              <div key={card.title} className="col-md-4 mb-4">
                <motion.div
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  className="card h-100 shadow-sm border-0"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(card.path)}
                >
                  <div className="card-body text-center">
                    <div className="mb-3 text-primary">{card.icon}</div>
                    <h5 className="fw-bold">{card.title}</h5>
                    <p className="text-muted small">{card.desc}</p>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </motion.div>
      ))}

      {filteredSections.length === 0 && (
        <p className="text-center text-muted mt-5">
          Menu tidak ditemukan 😢
        </p>
      )}
    </div>
  );
}
